import React, { Suspense, lazy } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import qs from 'qs';
import { getLanguage } from 'translation';
import Preloader from 'components/Preloader';
import Helmet from 'components/Helmet';
import MainLayout from 'layouts/MainLayout';
import LazyComponent from 'components/LazyComponent';
import { COLUMNS_LIMIT } from 'variables/main';
import getCurrentId from 'helpers/getCurrentId';
import { getLocalizedPath } from 'helpers/getLocale';
import { buildEntityUrl, encodeSlug } from 'utils/seo';

const Header = lazy(() => import('components/Header'));
const Footer = lazy(() => import('components/Footer'));
const LinearProgress = lazy(() => import('components/LinearProgress'));
const Questions = lazy(() => import('pages/Consultant/components/FAQ'));
const OtherConsultants = React.lazy(() => import('pages/Consultant/components/OtherConsultants'));
const ConsultantInformation = React.lazy(() => import('pages/Consultant/components/ConsultantInformation'));
const Reviews = React.lazy(() => import('components/Reviews'));

const ConsultantPage = ({ getInfo, getList, endPoint }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [lang] = React.useState(() => getLanguage().toUpperCase());
  const [currentId, setCurrentId] = React.useState(getCurrentId(location));
  const [consultant, setConsultant] = React.useState(null);
  const [consultants, setConsultants] = React.useState(null);
  const t = useTranslate('Consultant');

  const [isUserInformationVisible, setIsUserInformationVisible] = React.useState(false);
  const [isOtherConsultantsVisible, setIsOtherConsultantsVisible] = React.useState(false);
  const [isQuestionsVisible, setIsQuestionsVisible] = React.useState(false);

  const consultantInfo = useSelector((state) => state[endPoint].state[currentId]);
  const tHelmet = useTranslate('HELMET');

  const fetchConsultantsData = React.useCallback(async () => {
    const urlData = {
      offset: 0,
      limit: 4,
      fields: [
        '_id',
        `firstName${lang}`,
        `lastName${lang}`,
        'photoLinks',
        'pricePerSession',
        'experience',
        'experienceFrom',
        'services'
      ]
    };

    const queryString = qs.stringify(urlData);
    const similar = await dispatch(getList(queryString));
    if (similar instanceof Error) return;
    let filterExisted = (similar || []).filter((item) => item._id !== currentId);
    if (filterExisted.length > COLUMNS_LIMIT) filterExisted = filterExisted.slice(0, COLUMNS_LIMIT);
    setConsultants(filterExisted);
  }, [dispatch, currentId, lang, getList]);

  React.useEffect(() => {
    const satPageData = (obj) => {
      if (!obj) return;
      setConsultant(obj);
    };

    const fetchData = async () => {
      const resultInfo = await dispatch(getInfo(currentId));
      if (!resultInfo || resultInfo instanceof Error) {
        navigate('/');
        return;
      }
      await satPageData(resultInfo);
      fetchConsultantsData();
    };

    if (!currentId) {
      navigate('/');
      return;
    }

    if (consultantInfo) {
      satPageData(consultantInfo);
      return;
    }

    fetchData();
  }, [currentId, fetchConsultantsData, consultantInfo, dispatch, navigate, getInfo]);

  React.useEffect(() => {
    const newId = getCurrentId(location);
    if (newId !== currentId) {
      setCurrentId(newId);
    }
  }, [location, currentId]);

  const canonicalUrl = React.useMemo(() => {
    if (!consultant) return undefined;
    const type = endPoint === 'consultants' ? 'consultant' : 'mentor';
    // Use slug if available, otherwise generate from name
    const name = `${consultant[`firstName${lang}`]} ${consultant[`lastName${lang}`]}`;
    const slug = consultant.slug || encodeSlug(name);
    return buildEntityUrl(type, slug, consultant._id);
  }, [consultant, endPoint, lang]);

  if (!consultant) {
    return <Preloader />;
  }

  return (
    <>
      <Helmet
        meta={{
          title: t('ConsultateWithMeta', {
            name: consultant[`firstName${lang}`] + ' ' + consultant[`lastName${lang}`]
          }),
          description:
            endPoint === 'consultants'
              ? tHelmet('consultantDescription')
              : tHelmet('mentorDescription'),
          ogTitle: t('ConsultateWithMeta', {
            name: consultant[`firstName${lang}`] + ' ' + consultant[`lastName${lang}`]
          }),
          ogDescription:
            endPoint === 'consultants'
              ? tHelmet('consultantOgDescription')
              : tHelmet('mentorOgDescription')
        }}
        canonicalUrl={canonicalUrl}
      />
      <MainLayout>
        <Header />

        <Suspense fallback={<LinearProgress />}>
          <LazyComponent
            component={ConsultantInformation}
            endPoint={endPoint}
            setIsVisible={setIsUserInformationVisible}
            info={consultant}
          />
        </Suspense>

        {isUserInformationVisible && (
          <Suspense fallback={<LinearProgress />}>
            <LazyComponent
              component={Reviews}
              endPoint={endPoint}
              key={currentId}
              currentId={currentId}
            />
          </Suspense>
        )}

        {isOtherConsultantsVisible && (
          <Suspense fallback={<LinearProgress />}>
            <LazyComponent
              component={Questions}
              endPoint={endPoint}
              setIsVisible={setIsQuestionsVisible}
            />
          </Suspense>
        )}

        {isUserInformationVisible && (
          <Suspense fallback={<LinearProgress />}>
            <LazyComponent
              component={OtherConsultants}
              consultants={consultants}
              key={currentId}
              endPoint={endPoint}
              setIsVisible={setIsOtherConsultantsVisible}
              handleRedirect={() => navigate(getLocalizedPath(`/${endPoint}`))}
            />
          </Suspense>
        )}

        {isQuestionsVisible && (
          <Suspense fallback={<LinearProgress />}>
            <LazyComponent component={Footer} />
          </Suspense>
        )}
      </MainLayout>
    </>
  );
};

export default ConsultantPage;
